import { type FormEvent, useEffect, useId, useState } from 'react';

import { CONTACTS_PAGE } from '@/shared/contacts/api';
import {
  type Contact,
  type ContactGroup,
  MAX_GROUP_MEMBERS,
  peopleText,
} from '@/shared/contacts/types';
import { useContactGroups, useContactList } from '@/shared/contacts/useContacts';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { Input } from '@/shared/ui/Input';
import { PlusIcon, SearchIcon, UsersIcon } from '@/shared/ui/icons';
import { Spinner } from '@/shared/ui/Spinner';
import { formatDate } from '@/shared/utils/format';
import { useDebounced } from '@/shared/utils/useDebounced';

import { contactChanges, membersText, parseMembers, usesText } from './contactGroups';
import { RowMenu } from './RowMenu';
import {
  useCreateContact,
  useCreateGroup,
  useDeleteContact,
  useDeleteGroup,
  useUpdateContact,
  useUpdateGroup,
} from './useAdmin';

const dayFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium' });

/** How many addresses of a group the list shows before «y N más». */
const PREVIEW = 3;

/**
 * Administración → Contactos (contract v10.44, ADR 0038 of the back): the groups, and the
 * address book that «Para», «Copia» and «Copia oculta» suggest from.
 *
 * The book fills itself with every message sent from a mailbox, from here or from
 * Thunderbird; this screen is for what that cannot know: a name, a typo, somebody nobody
 * has written to yet. Contacts ask for no password. Groups do, like Destinos: one of them,
 * «Comerciales», is the triage destination of that name, the one advertising goes to when
 * somebody confirms it, so it comes first and says so, and cannot be renamed or deleted.
 */
export function ContactsTab() {
  return (
    <div className="flex flex-col gap-4">
      <GroupsCard />
      <ContactsCard />
    </div>
  );
}

function GroupsCard() {
  const groups = useContactGroups();
  const [editing, setEditing] = useState<ContactGroup | 'new' | null>(null);
  const [deleting, setDeleting] = useState<ContactGroup | null>(null);

  return (
    <Card
      title="Grupos"
      description="Para escribir a varias personas de una vez. Cada una lo recibe en copia oculta."
      padded={false}
      aside={
        <Button
          size="sm"
          variant="secondary"
          icon={<PlusIcon size={15} />}
          onClick={() => setEditing('new')}
        >
          Nuevo grupo
        </Button>
      }
    >
      {groups.isPending ? <Spinner variant="fill" label="Cargando los grupos" /> : null}
      {groups.isError ? (
        <ErrorMessage error={groups.error} className="m-4">
          No se pudieron cargar los grupos
        </ErrorMessage>
      ) : null}
      {groups.data ? (
        <ul className="divide-line-2 divide-y" aria-label="Grupos">
          {groups.data.map((group) => (
            <GroupRow
              key={group.id}
              group={group}
              onEdit={() => setEditing(group)}
              onDelete={() => setDeleting(group)}
            />
          ))}
        </ul>
      ) : null}
      {editing ? (
        <GroupDialog group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}
      {deleting ? <DeleteGroupDialog group={deleting} onClose={() => setDeleting(null)} /> : null}
    </Card>
  );
}

function GroupRow({
  group,
  onEdit,
  onDelete,
}: {
  group: ContactGroup;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const members = group.members ?? [];
  const count = group.member_count ?? members.length;
  const shown = members.slice(0, PREVIEW).map((member) => member.address);
  return (
    <li className="flex items-start justify-between gap-3 px-4 py-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="text-ink flex flex-wrap items-center gap-x-2 gap-y-1 text-[14.5px] font-medium">
          <UsersIcon size={15} className="text-ink-3 shrink-0" />
          {group.name}
          <span className="text-ink-3 text-[13px] font-normal">{peopleText(count)}</span>
          {group.system ? <Badge tone="neutral">destino del triaje</Badge> : null}
        </p>
        {group.system ? (
          <p className="text-ink-2 text-[13px] leading-snug">
            Es también el destino «Comerciales» del triaje: a estas direcciones se reenvía la
            publicidad de proveedores cuando alguien la confirma con «Correcto» o «Corregir a…». No
            se puede renombrar ni borrar.
          </p>
        ) : null}
        <p className="text-ink-3 text-[12.5px] leading-snug [overflow-wrap:anywhere]">
          {count === 0
            ? group.system
              ? 'Sin nadie todavía: mientras esté vacío, no se reenvía nada a los comerciales.'
              : 'Sin nadie todavía.'
            : `${shown.join(', ')}${count > shown.length ? ` y ${count - shown.length} más` : ''}`}
        </p>
      </div>
      <RowMenu
        label={`Acciones del grupo ${group.name}`}
        items={[
          { label: 'Editar', onSelect: onEdit },
          ...(group.system
            ? []
            : [{ label: 'Borrar', tone: 'danger' as const, onSelect: onDelete }]),
        ]}
      />
    </li>
  );
}

/**
 * A group, whole: its name and its addresses, one a line. Saved in one go (`PUT` replaces
 * every member), with the password of the elevated mode when the API asks for it.
 */
function GroupDialog({ group, onClose }: { group: ContactGroup | null; onClose: () => void }) {
  const [name, setName] = useState(group?.name ?? '');
  const [text, setText] = useState(membersText(group?.members ?? []));
  const [tried, setTried] = useState(false);
  const create = useCreateGroup(onClose);
  const update = useUpdateGroup(onClose);
  const save = group ? update : create;
  const id = useId();
  const parsed = parseMembers(text);
  const nameError = name.trim() ? undefined : 'Pon un nombre.';
  const system = Boolean(group?.system);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setTried(true);
    if (nameError || parsed.problem) return;
    const payload = { name: name.trim(), members: parsed.members };
    if (group) update.mutate({ id: group.id, payload });
    else create.mutate(payload);
  }

  return (
    <Dialog
      title={group ? `Grupo «${group.name}»` : 'Grupo nuevo'}
      description={
        system
          ? 'Lo que pongas aquí es a quién se reenvía la publicidad cuando alguien la confirma.'
          : undefined
      }
      size="md"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={`${id}-form`} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : group ? 'Guardar el grupo' : 'Crear el grupo'}
          </Button>
        </>
      }
    >
      <form id={`${id}-form`} className="flex flex-col gap-3" onSubmit={submit} noValidate>
        <Input
          label="Nombre"
          value={name}
          maxLength={120}
          readOnly={system}
          onChange={(event) => setName(event.target.value)}
          hint={
            system
              ? 'No se puede renombrar: es también un destino del triaje.'
              : 'Como se buscará al escribir en «Para»: «Tiendas», «Comerciales zona norte».'
          }
          error={tried ? nameError : undefined}
        />
        <div className="flex flex-col gap-1">
          <label htmlFor={`${id}-members`} className="text-ink-2 text-[13.5px] font-medium">
            Direcciones
          </label>
          <textarea
            id={`${id}-members`}
            className="field h-auto min-h-40 py-2 font-mono text-[13px]"
            rows={8}
            value={text}
            aria-describedby={`${id}-members-hint${tried && parsed.problem ? ` ${id}-members-error` : ''}`}
            aria-invalid={tried && parsed.problem ? true : undefined}
            onChange={(event) => setText(event.target.value)}
            placeholder={'ana@ferreteriacubas.com\nLuis Gil <luis@ferreteriacubas.com>'}
          />
          <p id={`${id}-members-hint`} className="text-ink-3 text-[12.5px] leading-snug">
            Una por línea, con el nombre delante si quieres. {parsed.members.length} de{' '}
            {MAX_GROUP_MEMBERS}.
          </p>
          {tried && parsed.problem ? (
            <p id={`${id}-members-error`} role="alert" className="text-danger text-[13px]">
              {parsed.problem}
            </p>
          ) : null}
        </div>
        {save.isError ? <ErrorMessage error={save.error}>No se pudo guardar</ErrorMessage> : null}
      </form>
    </Dialog>
  );
}

function DeleteGroupDialog({ group, onClose }: { group: ContactGroup; onClose: () => void }) {
  const remove = useDeleteGroup(onClose);
  return (
    <Dialog
      title={`¿Borrar el grupo «${group.name}»?`}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            data-autofocus
            onClick={() => remove.mutate(group.id)}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Borrando…' : 'Borrar el grupo'}
          </Button>
        </>
      }
    >
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Las direcciones siguen en la libreta. Los correos ya enviados a este grupo siguen diciendo a
        cuál se mandaron.
      </p>
      {remove.isError ? <ErrorMessage error={remove.error}>No se pudo borrar</ErrorMessage> : null}
    </Dialog>
  );
}

function ContactsCard() {
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [editing, setEditing] = useState<Contact | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Contact | null>(null);
  const searched = useDebounced(search.trim());
  const contacts = useContactList({ ...(searched ? { search: searched } : {}), offset });
  const total = contacts.data?.total ?? 0;
  const items = contacts.data?.items ?? [];
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + CONTACTS_PAGE, total);
  // A page left empty (its last contact deleted, here or by somebody else) has no footer
  // and so no «Anteriores»: back to the last page that has something.
  const emptyPage =
    contacts.data !== undefined &&
    !contacts.isPlaceholderData &&
    contacts.data.items.length === 0 &&
    offset > 0;
  useEffect(() => {
    if (emptyPage) setOffset(Math.floor(Math.max(0, total - 1) / CONTACTS_PAGE) * CONTACTS_PAGE);
  }, [emptyPage, total]);

  return (
    <Card
      title="Libreta de direcciones"
      description="Se llena sola con cada correo que sale de un buzón, desde aquí o desde Thunderbird. Cada persona ve los contactos de sus buzones y los añadidos a mano."
      padded={false}
      aside={
        <Button
          size="sm"
          variant="secondary"
          icon={<PlusIcon size={15} />}
          onClick={() => setEditing('new')}
        >
          Añadir contacto
        </Button>
      }
    >
      <div className="border-line-2 border-b px-4 py-3">
        <div className="relative max-w-md">
          <SearchIcon
            size={16}
            className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            aria-label="Buscar contacto"
            placeholder="Nombre, dirección o dominio"
            className="field w-full pl-9"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setOffset(0);
            }}
          />
        </div>
      </div>
      {contacts.isPending ? <Spinner variant="fill" label="Cargando los contactos" /> : null}
      {contacts.isError ? (
        <ErrorMessage error={contacts.error} className="m-4">
          No se pudieron cargar los contactos
        </ErrorMessage>
      ) : null}
      {contacts.data && items.length === 0 && offset === 0 ? (
        <div className="p-4">
          <EmptyState
            title={searched ? 'Ningún contacto así' : 'La libreta está vacía'}
            hint={
              searched
                ? 'Prueba con el principio del nombre, de la dirección o del dominio.'
                : 'Se llenará sola con el primer correo que salga de un buzón. También puedes añadir uno a mano.'
            }
          />
        </div>
      ) : null}
      {items.length > 0 ? (
        <>
          <ul className="divide-line-2 divide-y" aria-label="Contactos">
            {items.map((contact) => (
              <ContactRow
                key={contact.id}
                contact={contact}
                onEdit={() => setEditing(contact)}
                onDelete={() => setDeleting(contact)}
              />
            ))}
          </ul>
          <footer className="border-line-2 text-ink-3 flex items-center justify-between gap-2 border-t px-4 py-2 text-[13px]">
            <span className="tabular-nums">
              {from}–{to} de {total}
            </span>
            <span className="flex gap-2">
              <Button
                size="sm"
                variant="secondary"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - CONTACTS_PAGE))}
              >
                Anteriores
              </Button>
              <Button
                size="sm"
                variant="secondary"
                disabled={to >= total}
                onClick={() => setOffset(offset + CONTACTS_PAGE)}
              >
                Siguientes
              </Button>
            </span>
          </footer>
        </>
      ) : null}
      {editing ? (
        <ContactDialog
          contact={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {deleting ? (
        <DeleteContactDialog contact={deleting} onClose={() => setDeleting(null)} />
      ) : null}
    </Card>
  );
}

function ContactRow({
  contact,
  onEdit,
  onDelete,
}: {
  contact: Contact;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const mailboxes = contact.mailboxes ?? [];
  const facts = [
    usesText(contact.uses ?? 0),
    contact.last_used_at ? `el último, ${formatDate(dayFormat, contact.last_used_at)}` : null,
    mailboxes.length > 0 ? `desde ${mailboxes.join(', ')}` : null,
    contact.source === 'manual' ? 'añadido a mano' : null,
  ].filter(Boolean);
  return (
    <li className="flex items-start justify-between gap-3 px-4 py-2.5">
      <div className="flex min-w-0 flex-col [overflow-wrap:anywhere]">
        {contact.name ? (
          <span className="text-ink text-[14.5px] leading-5 font-medium">{contact.name}</span>
        ) : null}
        <span
          className={
            contact.name
              ? 'text-ink-2 text-[13.5px] leading-5'
              : 'text-ink text-[14.5px] leading-5 font-medium'
          }
        >
          {contact.address}
        </span>
        <span className="text-ink-3 text-[12.5px] leading-4">{facts.join(' · ')}</span>
      </div>
      <RowMenu
        label={`Acciones de ${contact.address}`}
        items={[
          { label: 'Corregir', onSelect: onEdit },
          { label: 'Borrar', tone: 'danger', onSelect: onDelete },
        ]}
      />
    </li>
  );
}

function ContactDialog({ contact, onClose }: { contact: Contact | null; onClose: () => void }) {
  const [address, setAddress] = useState(contact?.address ?? '');
  const [name, setName] = useState(contact?.name ?? '');
  const [tried, setTried] = useState(false);
  const create = useCreateContact();
  const update = useUpdateContact();
  const save = contact ? update : create;
  const id = useId();
  const addressError = /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(address.trim())
    ? undefined
    : 'Pon una dirección de correo.';

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setTried(true);
    if (addressError) return;
    if (!contact) {
      create.mutate({ address: address.trim(), name: name.trim() || null }, { onSuccess: onClose });
      return;
    }
    const change = contactChanges(contact, { address, name });
    if (Object.keys(change).length === 0) {
      onClose();
      return;
    }
    update.mutate({ id: contact.id, change }, { onSuccess: onClose });
  }

  return (
    <Dialog
      title={contact ? 'Corregir el contacto' : 'Contacto nuevo'}
      description={
        contact
          ? 'Un nombre corregido aquí no lo cambia lo que diga un correo después.'
          : 'Un contacto añadido a mano lo ve todo el mundo.'
      }
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={`${id}-form`} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : contact ? 'Guardar' : 'Añadir'}
          </Button>
        </>
      }
    >
      <form id={`${id}-form`} className="flex flex-col gap-3" onSubmit={submit} noValidate>
        <Input
          label="Dirección"
          type="email"
          value={address}
          autoComplete="off"
          onChange={(event) => setAddress(event.target.value)}
          error={tried ? addressError : undefined}
        />
        <Input
          label="Nombre"
          optional
          value={name}
          maxLength={255}
          onChange={(event) => setName(event.target.value)}
        />
        {save.isError ? <ErrorMessage error={save.error}>No se pudo guardar</ErrorMessage> : null}
      </form>
    </Dialog>
  );
}

function DeleteContactDialog({ contact, onClose }: { contact: Contact; onClose: () => void }) {
  const remove = useDeleteContact();
  return (
    <Dialog
      title={`¿Borrar ${contact.address} de la libreta?`}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={remove.isPending}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            data-autofocus
            onClick={() => remove.mutate(contact.id, { onSuccess: onClose })}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Borrando…' : 'Borrar'}
          </Button>
        </>
      }
    >
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Se lleva lo que se sabe de cuántas veces se le ha escrito. Si se le vuelve a escribir,
        vuelve a aparecer. No lo quita de ningún grupo: los grupos guardan direcciones, no
        contactos.
      </p>
      {remove.isError ? <ErrorMessage error={remove.error}>No se pudo borrar</ErrorMessage> : null}
    </Dialog>
  );
}
